import { useErrorInlineNotificationAPI } from '@/components/ErrorInlineNotification';
import { IMPORT_OPTIONS_SUCCESS_TOAST_MESSAGE_KEY } from '@/features/transaction_items/Detail/components/Drawer/ImportTransactionDetailOptionDrawer/constant';
import { ImportCsvForm } from '@/features/transaction_items/Detail/components/Drawer/ImportTransactionDetailOptionDrawer/ImportTransactionDetailOptionDrawer';
import { useTranslation } from '@/i18n';
import {
  GetImportExportHistoriesListSource,
  useImportCustomTransactionItemOptions,
} from 'ap-openapi';
import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

type UseSubmitImportOptionsParams = {
  transactionItemId: string;
  onClose: () => void;
};

export const useSubmitImportOptions = ({
  transactionItemId,
  onClose,
}: UseSubmitImportOptionsParams) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const setError = useErrorInlineNotificationAPI();
  const { mutateAsync: importOptions } =
    useImportCustomTransactionItemOptions();

  return useCallback(
    async (data: ImportCsvForm) => {
      const file = data.file[0];
      if (!file) return;

      const blob =
        file.originFileObj ??
        (file instanceof File || file instanceof Blob ? file : undefined);
      if (!blob) return;

      try {
        await importOptions({
          id: transactionItemId,
          data: { file: blob },
        });

        onClose();
        sessionStorage.setItem(
          IMPORT_OPTIONS_SUCCESS_TOAST_MESSAGE_KEY,
          t(
            'transaction_items.detail.options.drawer.import_file.import_success'
          )
        );
        navigate(
          {
            pathname: '/import_export_histories',
            search: `?source=${GetImportExportHistoriesListSource.custom_transaction_item_options}`,
          },
          {
            state: { from: location.pathname },
          }
        );
      } catch {
        setError(
          new Error(
            t(
              'transaction_items.detail.options.drawer.import_file.import_error'
            )
          )
        );
      }
    },
    [
      importOptions,
      transactionItemId,
      onClose,
      navigate,
      location.pathname,
      t,
      setError,
    ]
  );
};
